import { useRef } from 'react';
import { TableProps } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { usePageOverflow } from '@app/hooks/usePageOverflow';

import * as S from './BaseTable.styles';

export type BaseTableProps<T> = TableProps<T> & {
  light?: boolean;
};

export const BaseTable = <T extends object>({ pagination, onChange, loading, ...rest }: BaseTableProps<T>) => {
  // Client-side tables leave `current` undefined and antd already keeps them in range.
  const config =
    pagination && typeof pagination === 'object' && typeof pagination.current === 'number' ? pagination : undefined;
  const isLoading = typeof loading === 'object' ? loading.spinning !== false : !!loading;

  // Parents read sort state off the `sorter` argument, so a synthetic change must replay the last one.
  const lastChangeArgs = useRef<{
    filters: Record<string, FilterValue | null>;
    sorter: SorterResult<T> | SorterResult<T>[];
  }>({ filters: {}, sorter: [] });

  const handleChange: TableProps<T>['onChange'] = (nextPagination, filters, sorter, extra) => {
    lastChangeArgs.current = { filters, sorter };
    onChange?.(nextPagination, filters, sorter, extra);
  };

  const handleClamp = (lastPage: number) => {
    if (!config || !onChange) {
      return;
    }

    const { filters, sorter } = lastChangeArgs.current;
    onChange({ ...config, current: lastPage }, filters, sorter, { action: 'paginate', currentDataSource: [] });
  };

  const { lastPage, isOverflow } = usePageOverflow({
    current: config?.current,
    pageSize: config?.pageSize,
    total: config?.total,
    disabled: !config || isLoading,
    onClamp: handleClamp,
  });

  const resolvedPagination = config && isOverflow ? { ...config, current: lastPage } : pagination;

  return <S.Table pagination={resolvedPagination} onChange={handleChange} loading={loading} {...rest} />;
};
